import React, { useEffect, useState, useCallback } from 'react';
import {
  Box,
  Card,
  CardContent,
  Typography,
  IconButton,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  MenuItem,
  Paper,
  Tooltip,
  Slider,
  Switch,
  FormControlLabel,
  LinearProgress,
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import SwapHorizIcon from '@mui/icons-material/SwapHoriz';
import ArrowUpwardIcon from '@mui/icons-material/ArrowUpward';
import ArrowDownwardIcon from '@mui/icons-material/ArrowDownward';
import SchoolIcon from '@mui/icons-material/School';
import VisibilityIcon from '@mui/icons-material/Visibility';
import NavigateNextIcon from '@mui/icons-material/NavigateNext';
import CloseIcon from '@mui/icons-material/Close';
import {
  fetchLanguages, createLanguage, updateLanguage, deleteLanguage,
  fetchSchoolYears, createSchoolYear, updateSchoolYear, deleteSchoolYear,
  fetchLectures, createLecture, updateLecture, deleteLecture,
  createWord, fetchWords, updateWord, deleteWord,
  fetchLanguageMasters
} from '../utils/contentApi';
import { useNotification } from '../hooks/useNotification';
import Notification from '../components/Notification';

// Performance Pie Chart Component
const PerformancePieChart = ({ correctCount = 0, challengedCount = 0, size = 32, inverted = false }) => {
  const total = correctCount + challengedCount;
  const percentage = total > 0 ? Math.round((correctCount / total) * 100) : 0;

  // Calculate pie chart segments
  const correctAngle = total > 0 ? (correctCount / total) * 360 : 0;

  // Color based on performance
  const getColor = (pct, isInverted) => {
    if (isInverted) {
      // For inverted (selected) state, use white for the pie chart
      return '#ffffff';
    }
    if (pct >= 80) return '#10b981'; // green
    if (pct >= 60) return '#f59e0b'; // yellow
    return '#ef4444'; // red
  };

  const color = getColor(percentage, inverted);
  const textColor = inverted ? '#374151' : color; // Dark grey for selected, otherwise match pie color
  const bgColor = inverted ? 'rgba(255, 255, 255, 0.3)' : '#e5e7eb';
  const strokeColor = inverted ? 'rgba(255, 255, 255, 0.5)' : '#d1d5db';
  const centerFill = inverted ? 'white' : 'white';
  const emptyColor = inverted ? 'rgba(255, 255, 255, 0.3)' : '#f3f4f6';

  // Create SVG pie chart
  const radius = size / 2;
  const center = radius;
  const pieRadius = radius - 2;

  // Calculate arc path for correct answers
  const correctRad = (correctAngle * Math.PI) / 180;
  const x = center + pieRadius * Math.sin(correctRad);
  const y = center - pieRadius * Math.cos(correctRad);
  const largeArc = correctAngle > 180 ? 1 : 0;

  const pieChart = total > 0 ? (
    <svg width={size} height={size} viewBox={`0 0 ${size} ${size}`}>
      {/* Background circle (incorrect/challenged) */}
      <circle
        cx={center}
        cy={center}
        r={pieRadius}
        fill={bgColor}
        stroke={strokeColor}
        strokeWidth="1"
      />
      {/* Correct answers arc */}
      {correctCount > 0 && (
        <path
          d={`M ${center} ${center - pieRadius} A ${pieRadius} ${pieRadius} 0 ${largeArc} 1 ${x} ${y} L ${center} ${center} Z`}
          fill={color}
          stroke={color}
          strokeWidth="1"
        />
      )}
      {/* Center circle for donut effect */}
      <circle
        cx={center}
        cy={center}
        r={pieRadius * 0.6}
        fill={centerFill}
      />
      {/* Percentage text */}
      <text
        x={center}
        y={center}
        textAnchor="middle"
        dominantBaseline="middle"
        fontSize={size * 0.35}
        fontWeight="600"
        fill={textColor}
      >
        {percentage}
      </text>
    </svg>
  ) : (
    <svg width={size} height={size} viewBox={`0 0 ${size} ${size}`}>
      <circle
        cx={center}
        cy={center}
        r={pieRadius}
        fill={emptyColor}
        stroke={strokeColor}
        strokeWidth="1"
      />
      <circle
        cx={center}
        cy={center}
        r={pieRadius * 0.6}
        fill={centerFill}
      />
      <text
        x={center}
        y={center}
        textAnchor="middle"
        dominantBaseline="middle"
        fontSize={size * 0.35}
        fontWeight="600"
        fill={inverted ? '#ffffff' : '#9ca3af'}
      >
        -
      </text>
    </svg>
  );

  return (
    <Tooltip
      title={total > 0 ? `${correctCount} correct / ${challengedCount} challenged (${percentage}%)` : 'No practice data'}
      arrow
    >
      <Box sx={{ display: 'inline-block' }}>
        {pieChart}
      </Box>
    </Tooltip>
  );
};

const ContentPage = () => {
  // Data
  const [languages, setLanguages] = useState([]);
  const [languageMasters, setLanguageMasters] = useState([]);
  const [schoolYears, setSchoolYears] = useState([]);
  const [lectures, setLectures] = useState([]);
  const [words, setWords] = useState([]);

  // Selection state
  const [selectedLanguage, setSelectedLanguage] = useState(null);
  const [selectedSchoolYear, setSelectedSchoolYear] = useState(null);
  const [selectedLecture, setSelectedLecture] = useState(null);

  // Loading states
  const [loading, setLoading] = useState(true);
  const [loadingWords, setLoadingWords] = useState(false);

  // Filter and sort states
  const [searchFilter, setSearchFilter] = useState('');
  const [sortBy, setSortBy] = useState('native'); // 'native', 'foreign', 'performance'
  const [sortOrder, setSortOrder] = useState('asc'); // 'asc' or 'desc'

  // Dialog states
  const [addLanguageDialog, setAddLanguageDialog] = useState(false);
  const [editLanguageDialog, setEditLanguageDialog] = useState(null);
  const [addSchoolYearDialog, setAddSchoolYearDialog] = useState(false);
  const [editSchoolYearDialog, setEditSchoolYearDialog] = useState(null);
  const [addLectureDialog, setAddLectureDialog] = useState(false);
  const [editLectureDialog, setEditLectureDialog] = useState(null);
  const [addWordDialog, setAddWordDialog] = useState(false);
  const [editWordDialog, setEditWordDialog] = useState(null);
  const [deleteConfirmDialog, setDeleteConfirmDialog] = useState(null);
  const [learningModeDialog, setLearningModeDialog] = useState(null);

  const notify = useNotification();

  const loadLanguageMasters = useCallback(async () => {
    try {
      const data = await fetchLanguageMasters();
      setLanguageMasters(data);
    } catch (err) {
      console.error('Failed to load language masters:', err);
    }
  }, []);

  const loadLanguages = useCallback(async () => {
    try {
      setLoading(true);
      const data = await fetchLanguages();
      setLanguages(data);
    } catch (err) {
      console.error('Failed to load languages:', err);
    } finally {
      setLoading(false);
    }
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const loadSchoolYears = useCallback(async () => {
    try {
      const data = await fetchSchoolYears();
      const filtered = data.filter(sy =>
        sy.language === selectedLanguage._id || sy.language?._id === selectedLanguage._id
      );
      setSchoolYears(filtered);
    } catch (err) {
      console.error('Failed to load school years:', err);
    }
  }, [selectedLanguage]); // eslint-disable-line react-hooks/exhaustive-deps

  const loadLectures = useCallback(async () => {
    try {
      const data = await fetchLectures();
      const filtered = data.filter(l =>
        l.schoolYear === selectedSchoolYear._id || l.schoolYear?._id === selectedSchoolYear._id
      );
      setLectures(filtered);
    } catch (err) {
      console.error('Failed to load lectures:', err);
    }
  }, [selectedSchoolYear]); // eslint-disable-line react-hooks/exhaustive-deps

  const loadWords = useCallback(async () => {
    try {
      setLoadingWords(true);
      const data = await fetchWords(selectedLecture._id);
      setWords(data);
    } catch (err) {
      console.error('Failed to load words:', err);
    } finally {
      setLoadingWords(false);
    }
  }, [selectedLecture]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    loadLanguageMasters();
    loadLanguages();
  }, [loadLanguageMasters, loadLanguages]);

  useEffect(() => {
    if (selectedLanguage) {
      loadSchoolYears();
    } else {
      setSchoolYears([]);
      setSelectedSchoolYear(null);
    }
  }, [selectedLanguage, loadSchoolYears]);

  useEffect(() => {
    if (selectedSchoolYear) {
      loadLectures();
    } else {
      setLectures([]);
      setSelectedLecture(null);
    }
  }, [selectedSchoolYear, loadLectures]);

  useEffect(() => {
    if (selectedLecture) {
      loadWords();
    } else {
      setWords([]);
    }
  }, [selectedLecture, loadWords]);

  // Language handlers
  const handleAddLanguage = async (formData) => {
    try {
      const newLanguage = await createLanguage(formData.name, formData.nativeLanguage, formData.foreignLanguage);
      await loadLanguages();
      setAddLanguageDialog(false);
      // Set the new language as selected and clear dependent selections
      setSelectedLanguage(newLanguage);
      setSelectedSchoolYear(null);
      setSelectedLecture(null);
      setWords([]);
      notify.success('Language added successfully');
    } catch (err) {
      notify.error(err.message || 'Failed to add language');
    }
  };

  const handleUpdateLanguage = async (id, formData) => {
    try {
      await updateLanguage(id, formData);
      await loadLanguages();
      // Update selected language if it was edited
      if (selectedLanguage?._id === id) {
        setSelectedLanguage({ ...selectedLanguage, ...formData });
      }
      setEditLanguageDialog(null);
      notify.success('Language updated successfully');
    } catch (err) {
      notify.error(err.message || 'Failed to update language');
    }
  };

  const handleDeleteLanguage = async (id) => {
    try {
      await deleteLanguage(id);
      if (selectedLanguage?._id === id) {
        setSelectedLanguage(null);
      }
      await loadLanguages();
      setDeleteConfirmDialog(null);
      notify.success('Language deleted successfully');
    } catch (err) {
      notify.error(err.message || 'Failed to delete language');
    }
  };

  // School Year handlers
  const handleAddSchoolYear = async (name) => {
    try {
      await createSchoolYear(name, selectedLanguage._id);
      await loadSchoolYears();
      setAddSchoolYearDialog(false);
      notify.success('School year added successfully');
    } catch (err) {
      notify.error(err.message || 'Failed to add school year');
    }
  };

  const handleUpdateSchoolYear = async (id, name) => {
    try {
      await updateSchoolYear(id, name);
      await loadSchoolYears();
      if (selectedSchoolYear?._id === id) {
        setSelectedSchoolYear({ ...selectedSchoolYear, name });
      }
      setEditSchoolYearDialog(null);
      notify.success('School year updated successfully');
    } catch (err) {
      notify.error(err.message || 'Failed to update school year');
    }
  };

  const handleDeleteSchoolYear = async (id) => {
    try {
      await deleteSchoolYear(id);
      if (selectedSchoolYear?._id === id) {
        setSelectedSchoolYear(null);
      }
      await loadSchoolYears();
      setDeleteConfirmDialog(null);
      notify.success('School year deleted successfully');
    } catch (err) {
      notify.error(err.message || 'Failed to delete school year');
    }
  };

  // Lecture handlers
  const handleAddLecture = async (name) => {
    try {
      await createLecture(name, selectedSchoolYear._id);
      await loadLectures();
      setAddLectureDialog(false);
      notify.success('Lecture added successfully');
    } catch (err) {
      notify.error(err.message || 'Failed to add lecture');
    }
  };

  const handleUpdateLecture = async (id, name) => {
    try {
      await updateLecture(id, name);
      await loadLectures();
      if (selectedLecture?._id === id) {
        setSelectedLecture({ ...selectedLecture, name });
      }
      setEditLectureDialog(null);
      notify.success('Lecture updated successfully');
    } catch (err) {
      notify.error(err.message || 'Failed to update lecture');
    }
  };

  const handleDeleteLecture = async (id) => {
    try {
      await deleteLecture(id);
      if (selectedLecture?._id === id) {
        setSelectedLecture(null);
      }
      await loadLectures();
      setDeleteConfirmDialog(null);
      notify.success('Lecture deleted successfully');
    } catch (err) {
      notify.error(err.message || 'Failed to delete lecture');
    }
  };

  // Word handlers
  const handleAddWord = async (nativeWord, foreignWord) => {
    try {
      await createWord(nativeWord, foreignWord, selectedLecture._id);
      await loadWords();
      setAddWordDialog(false);
      notify.success('Word added successfully');
    } catch (err) {
      notify.error(err.message || 'Failed to add word');
    }
  };

  const handleAddWordAndNew = async (nativeWord, foreignWord) => {
    try {
      await createWord(nativeWord, foreignWord, selectedLecture._id);
      await loadWords();
      notify.success('Word added successfully');
      // Don't close the dialog - it stays open for next word
    } catch (err) {
      notify.error(err.message || 'Failed to add word');
    }
  };

  const handleUpdateWord = async (wordId, nativeWord, foreignWord) => {
    try {
      await updateWord(wordId, { nativeWord, foreignWord });
      await loadWords();
      setEditWordDialog(null);
      notify.success('Word updated successfully');
    } catch (err) {
      notify.error(err.message || 'Failed to update word');
    }
  };

  const handleDeleteWord = async (wordId) => {
    try {
      await deleteWord(wordId);
      await loadWords();
      setDeleteConfirmDialog(null);
      notify.success('Word deleted successfully');
    } catch (err) {
      notify.error(err.message || 'Failed to delete word');
    }
  };

  // Filter and sort words
  const getFilteredAndSortedWords = () => {
    let filtered = words;

    // Apply search filter
    if (searchFilter.trim()) {
      const searchLower = searchFilter.toLowerCase();
      filtered = filtered.filter(word =>
        word.nativeWord.toLowerCase().includes(searchLower) ||
        word.foreignWord.toLowerCase().includes(searchLower)
      );
    }

    // Apply sorting
    const sorted = [...filtered].sort((a, b) => {
      let comparison = 0;

      if (sortBy === 'native') {
        comparison = a.nativeWord.localeCompare(b.nativeWord);
      } else if (sortBy === 'foreign') {
        comparison = a.foreignWord.localeCompare(b.foreignWord);
      } else if (sortBy === 'performance') {
        const aTotal = (a.correctCount || 0) + (a.challengedCount || 0);
        const bTotal = (b.correctCount || 0) + (b.challengedCount || 0);
        const aPerf = aTotal > 0 ? (a.correctCount || 0) / aTotal : 0;
        const bPerf = bTotal > 0 ? (b.correctCount || 0) / bTotal : 0;
        comparison = aPerf - bPerf;
      }

      // Apply sort order (reverse if descending)
      return sortOrder === 'asc' ? comparison : -comparison;
    });

    return sorted;
  };

  return (
    <Box sx={{ display: 'flex', height: 'calc(100vh - 100px)', gap: 2 }}>
      {/* Left Sidebar - 50% */}
      <Box sx={{ width: '50%', display: 'flex', flexDirection: 'column', gap: 2 }}>

        {/* Languages Section - 33% */}
        <Paper sx={{ flex: 1, display: 'flex', flexDirection: 'column', overflow: 'hidden' }}>
          <Box sx={{ p: 2, borderBottom: 1, borderColor: 'divider', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <Typography variant="h6" sx={{ fontWeight: 500 }}>Languages</Typography>
            <IconButton size="small" color="primary" onClick={() => setAddLanguageDialog(true)}>
              <AddIcon />
            </IconButton>
          </Box>
          <Box sx={{ flex: 1, overflow: 'auto', p: 2 }}>
            {loading ? (
              <Typography variant="body2" color="text.secondary">Loading...</Typography>
            ) : languages.length === 0 ? (
              <Typography variant="body2" color="text.secondary">No languages yet</Typography>
            ) : (
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                {languages.map((lang) => {
                  const isSelected = selectedLanguage?._id === lang._id;
                  return (
                    <Card
                      key={lang._id}
                      variant="outlined"
                      sx={{
                        bgcolor: isSelected ? 'primary.main' : 'background.paper',
                        borderColor: isSelected ? 'primary.main' : 'divider',
                        borderWidth: isSelected ? 2 : 1,
                        cursor: 'pointer',
                        '&:hover': {
                          bgcolor: isSelected ? 'primary.main' : 'action.hover',
                        },
                      }}
                      onClick={() => {
                        setSelectedLanguage(lang);
                        setSelectedSchoolYear(null);
                        setSelectedLecture(null);
                        setWords([]);
                      }}
                    >
                      <CardContent sx={{ py: 1.5, '&:last-child': { pb: 1.5 } }}>
                        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                          <Box>
                            <Typography
                              variant="subtitle1"
                              sx={{
                                fontWeight: 500,
                                color: isSelected ? 'primary.contrastText' : 'text.primary'
                              }}
                            >
                              {lang.name}
                            </Typography>
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                              <span className={`fi fi-${lang.nativeLanguage?.iso?.toLowerCase()}`} style={{ fontSize: '1em' }}></span>
                              <Typography
                                variant="caption"
                                sx={{ color: isSelected ? 'primary.contrastText' : 'text.secondary' }}
                              >
                                {lang.nativeLanguage?.name || 'N/A'}
                              </Typography>
                              <Typography
                                variant="caption"
                                sx={{ color: isSelected ? 'primary.contrastText' : 'text.secondary', mx: 0.5 }}
                              >
                                →
                              </Typography>
                              <span className={`fi fi-${lang.foreignLanguage?.iso?.toLowerCase()}`} style={{ fontSize: '1em' }}></span>
                              <Typography
                                variant="caption"
                                sx={{ color: isSelected ? 'primary.contrastText' : 'text.secondary' }}
                              >
                                {lang.foreignLanguage?.name || 'N/A'}
                              </Typography>
                            </Box>
                          </Box>
                          <Box sx={{ display: 'flex', gap: 0.5 }} onClick={(e) => e.stopPropagation()}>
                            <IconButton
                              size="small"
                              onClick={() => setEditLanguageDialog(lang)}
                              sx={{ color: isSelected ? 'primary.contrastText' : 'inherit' }}
                            >
                              <EditIcon fontSize="small" />
                            </IconButton>
                            <IconButton
                              size="small"
                              onClick={() => setDeleteConfirmDialog({ type: 'language', id: lang._id, name: lang.name })}
                              sx={{ color: isSelected ? 'primary.contrastText' : 'inherit' }}
                            >
                              <DeleteIcon fontSize="small" />
                            </IconButton>
                          </Box>
                        </Box>
                      </CardContent>
                    </Card>
                  );
                })}
              </Box>
            )}
          </Box>
        </Paper>

        {/* School Years Section - 33% */}
        <Paper sx={{ flex: 1, display: 'flex', flexDirection: 'column', overflow: 'hidden' }}>
          <Box sx={{ p: 2, borderBottom: 1, borderColor: 'divider', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <Typography variant="h6" sx={{ fontWeight: 500 }}>Class</Typography>
            {selectedLanguage && (
              <IconButton size="small" color="primary" onClick={() => setAddSchoolYearDialog(true)}>
                <AddIcon />
              </IconButton>
            )}
          </Box>
          <Box sx={{ flex: 1, overflow: 'auto', p: 2 }}>
            {!selectedLanguage ? (
              <Typography variant="body2" color="text.secondary">Select a language first</Typography>
            ) : schoolYears.length === 0 ? (
              <Typography variant="body2" color="text.secondary">No classes yet</Typography>
            ) : (
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                {schoolYears.map((year) => {
                  const isSelected = selectedSchoolYear?._id === year._id;
                  return (
                    <Card
                      key={year._id}
                      variant="outlined"
                      sx={{
                        bgcolor: isSelected ? 'primary.main' : 'background.paper',
                        borderColor: isSelected ? 'primary.main' : 'divider',
                        borderWidth: isSelected ? 2 : 1,
                        cursor: 'pointer',
                        '&:hover': {
                          bgcolor: isSelected ? 'primary.main' : 'action.hover',
                        },
                      }}
                      onClick={() => {
                        setSelectedSchoolYear(year);
                        setSelectedLecture(null);
                        setWords([]);
                      }}
                    >
                      <CardContent sx={{ py: 1.5, '&:last-child': { pb: 1.5 } }}>
                        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                          <Typography
                            variant="subtitle1"
                            sx={{
                              fontWeight: 500,
                              color: isSelected ? 'primary.contrastText' : 'text.primary'
                            }}
                          >
                            {year.name}
                          </Typography>
                          <Box sx={{ display: 'flex', gap: 0.5 }} onClick={(e) => e.stopPropagation()}>
                            <IconButton
                              size="small"
                              onClick={() => setEditSchoolYearDialog(year)}
                              sx={{ color: isSelected ? 'primary.contrastText' : 'inherit' }}
                            >
                              <EditIcon fontSize="small" />
                            </IconButton>
                            <IconButton
                              size="small"
                              onClick={() => setDeleteConfirmDialog({ type: 'schoolYear', id: year._id, name: year.name })}
                              sx={{ color: isSelected ? 'primary.contrastText' : 'inherit' }}
                            >
                              <DeleteIcon fontSize="small" />
                            </IconButton>
                          </Box>
                        </Box>
                      </CardContent>
                    </Card>
                  );
                })}
              </Box>
            )}
          </Box>
        </Paper>

        {/* Lectures Section - 33% */}
        <Paper sx={{ flex: 1, display: 'flex', flexDirection: 'column', overflow: 'hidden' }}>
          <Box sx={{ p: 2, borderBottom: 1, borderColor: 'divider', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <Typography variant="h6" sx={{ fontWeight: 500 }}>Lecture</Typography>
            {selectedSchoolYear && (
              <IconButton size="small" color="primary" onClick={() => setAddLectureDialog(true)}>
                <AddIcon />
              </IconButton>
            )}
          </Box>
          <Box sx={{ flex: 1, overflow: 'auto', p: 2 }}>
            {!selectedSchoolYear ? (
              <Typography variant="body2" color="text.secondary">Select a class first</Typography>
            ) : lectures.length === 0 ? (
              <Typography variant="body2" color="text.secondary">No lectures yet</Typography>
            ) : (
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                {lectures.map((lecture) => {
                  const isSelected = selectedLecture?._id === lecture._id;

                  // Calculate average performance for this lecture
                  const lectureWords = lecture.words || [];
                  const totalCorrect = lectureWords.reduce((sum, word) => sum + (word.correctCount || 0), 0);
                  const totalChallenged = lectureWords.reduce((sum, word) => sum + (word.challengedCount || 0), 0);

                  return (
                    <Card
                      key={lecture._id}
                      variant="outlined"
                      sx={{
                        bgcolor: isSelected ? 'primary.main' : 'background.paper',
                        borderColor: isSelected ? 'primary.main' : 'divider',
                        borderWidth: isSelected ? 2 : 1,
                        cursor: 'pointer',
                        '&:hover': {
                          bgcolor: isSelected ? 'primary.main' : 'action.hover',
                        },
                      }}
                      onClick={() => setSelectedLecture(lecture)}
                    >
                      <CardContent sx={{ py: 1.5, '&:last-child': { pb: 1.5 } }}>
                        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flex: 1 }}>
                            <PerformancePieChart
                              correctCount={totalCorrect}
                              challengedCount={totalChallenged}
                              size={28}
                              inverted={isSelected}
                            />
                            <Typography
                              variant="subtitle1"
                              sx={{
                                fontWeight: 500,
                                color: isSelected ? 'primary.contrastText' : 'text.primary'
                              }}
                            >
                              {lecture.name}
                            </Typography>
                          </Box>
                          <Box sx={{ display: 'flex', gap: 0.5 }} onClick={(e) => e.stopPropagation()}>
                            <Tooltip title="Learning Mode" arrow>
                              <IconButton
                                size="small"
                                onClick={() => setLearningModeDialog({ lecture, language: selectedLanguage })}
                                sx={{ color: isSelected ? 'primary.contrastText' : 'inherit' }}
                              >
                                <SchoolIcon fontSize="small" />
                              </IconButton>
                            </Tooltip>
                            <IconButton
                              size="small"
                              onClick={() => setEditLectureDialog(lecture)}
                              sx={{ color: isSelected ? 'primary.contrastText' : 'inherit' }}
                            >
                              <EditIcon fontSize="small" />
                            </IconButton>
                            <IconButton
                              size="small"
                              onClick={() => setDeleteConfirmDialog({ type: 'lecture', id: lecture._id, name: lecture.name })}
                              sx={{ color: isSelected ? 'primary.contrastText' : 'inherit' }}
                            >
                              <DeleteIcon fontSize="small" />
                            </IconButton>
                          </Box>
                        </Box>
                      </CardContent>
                    </Card>
                  );
                })}
              </Box>
            )}
          </Box>
        </Paper>
      </Box>

      {/* Right Side - Words Area - 50% */}
      <Paper sx={{ width: '50%', display: 'flex', flexDirection: 'column', overflow: 'hidden' }}>
        <Box sx={{ p: 2, borderBottom: 1, borderColor: 'divider' }}>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: selectedLecture ? 2 : 0 }}>
            <Box>
              <Typography variant="h6" sx={{ fontWeight: 500 }}>Vocabulary</Typography>
              {selectedLecture && (
                <Typography variant="caption" color="text.secondary">
                  {selectedLanguage?.nativeLanguage?.name || 'Native'} → {selectedLanguage?.foreignLanguage?.name || 'Foreign'}
                </Typography>
              )}
            </Box>
            {selectedLecture && (
              <Button
                variant="contained"
                size="small"
                startIcon={<AddIcon />}
                onClick={() => setAddWordDialog(true)}
              >
                Add Word
              </Button>
            )}
          </Box>
          {selectedLecture && words.length > 0 && (
            <Box sx={{ display: 'flex', gap: 1 }}>
              <TextField
                size="small"
                placeholder="Search words..."
                value={searchFilter}
                onChange={(e) => setSearchFilter(e.target.value)}
                sx={{ flex: 1 }}
              />
              <TextField
                select
                size="small"
                value={sortBy}
                onChange={(e) => setSortBy(e.target.value)}
                sx={{ minWidth: 150 }}
              >
                <MenuItem value="native">Sort by {selectedLanguage?.nativeLanguage?.name || 'Native'}</MenuItem>
                <MenuItem value="foreign">Sort by {selectedLanguage?.foreignLanguage?.name || 'Foreign'}</MenuItem>
                <MenuItem value="performance">Sort by Performance</MenuItem>
              </TextField>
              <Box sx={{ display: 'flex', border: 1, borderColor: 'divider', borderRadius: 1, overflow: 'hidden' }}>
                <IconButton
                  size="small"
                  onClick={() => setSortOrder('asc')}
                  sx={{
                    borderRadius: 0,
                    bgcolor: sortOrder === 'asc' ? 'primary.main' : 'transparent',
                    color: sortOrder === 'asc' ? 'primary.contrastText' : 'text.secondary',
                    '&:hover': {
                      bgcolor: sortOrder === 'asc' ? 'primary.dark' : 'action.hover',
                    },
                  }}
                >
                  <ArrowUpwardIcon fontSize="small" />
                </IconButton>
                <IconButton
                  size="small"
                  onClick={() => setSortOrder('desc')}
                  sx={{
                    borderRadius: 0,
                    bgcolor: sortOrder === 'desc' ? 'primary.main' : 'transparent',
                    color: sortOrder === 'desc' ? 'primary.contrastText' : 'text.secondary',
                    '&:hover': {
                      bgcolor: sortOrder === 'desc' ? 'primary.dark' : 'action.hover',
                    },
                  }}
                >
                  <ArrowDownwardIcon fontSize="small" />
                </IconButton>
              </Box>
            </Box>
          )}
        </Box>
        <Box sx={{ flex: 1, overflow: 'auto', p: 2 }}>
          {!selectedLecture ? (
            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', height: '100%' }}>
              <Typography variant="body1" color="text.secondary">
                Select a lecture to view vocabulary
              </Typography>
            </Box>
          ) : loadingWords ? (
            <Typography variant="body2" color="text.secondary">Loading words...</Typography>
          ) : words.length === 0 ? (
            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', height: '100%' }}>
              <Typography variant="body2" color="text.secondary">
                No words yet. Click "Add Word" to get started.
              </Typography>
            </Box>
          ) : getFilteredAndSortedWords().length === 0 ? (
            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', height: '100%' }}>
              <Typography variant="body2" color="text.secondary">
                No words match your search "{searchFilter}"
              </Typography>
            </Box>
          ) : (
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
              {getFilteredAndSortedWords().map((word) => (
                <Card
                  key={word._id}
                  variant="outlined"
                  sx={{
                    '&:hover': {
                      borderColor: 'primary.main',
                      bgcolor: 'action.hover',
                    }
                  }}
                >
                  <CardContent sx={{ py: 1.5, '&:last-child': { pb: 1.5 } }}>
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                      <Box sx={{ display: 'flex', gap: 3, flex: 1 }}>
                        <Box sx={{ flex: 1 }}>
                          <Typography variant="caption" color="text.secondary" sx={{ textTransform: 'uppercase', fontSize: '0.65rem' }}>
                            {selectedLanguage?.nativeLanguage?.name || 'Native'}
                          </Typography>
                          <Typography variant="body1" sx={{ fontWeight: 500 }}>
                            {word.nativeWord}
                          </Typography>
                        </Box>
                        <Box sx={{ flex: 1 }}>
                          <Typography variant="caption" color="text.secondary" sx={{ textTransform: 'uppercase', fontSize: '0.65rem' }}>
                            {selectedLanguage?.foreignLanguage?.name || 'Foreign'}
                          </Typography>
                          <Typography variant="body1" sx={{ fontWeight: 500 }}>
                            {word.foreignWord}
                          </Typography>
                        </Box>
                      </Box>
                      <Box sx={{ display: 'flex', gap: 0.5, alignItems: 'center' }}>
                        <PerformancePieChart
                          correctCount={word.correctCount || 0}
                          challengedCount={word.challengedCount || 0}
                          size={32}
                        />
                        <IconButton size="small" onClick={() => setEditWordDialog(word)}>
                          <EditIcon fontSize="small" />
                        </IconButton>
                        <IconButton size="small" onClick={() => setDeleteConfirmDialog({ type: 'word', id: word._id, name: `${word.nativeWord} - ${word.foreignWord}` })}>
                          <DeleteIcon fontSize="small" />
                        </IconButton>
                      </Box>
                    </Box>
                  </CardContent>
                </Card>
              ))}
            </Box>
          )}
        </Box>
      </Paper>

      {/* Dialogs */}
      <LanguageDialog
        open={addLanguageDialog || !!editLanguageDialog}
        language={editLanguageDialog}
        languageMasters={languageMasters}
        onClose={() => {
          setAddLanguageDialog(false);
          setEditLanguageDialog(null);
        }}
        onSave={(formData) => {
          if (editLanguageDialog) {
            handleUpdateLanguage(editLanguageDialog._id, formData);
          } else {
            handleAddLanguage(formData);
          }
        }}
      />

      <SimpleDialog
        open={addSchoolYearDialog || !!editSchoolYearDialog}
        title={editSchoolYearDialog ? 'Edit Class' : 'Add Class'}
        label="Class Name"
        value={editSchoolYearDialog?.name || ''}
        onClose={() => {
          setAddSchoolYearDialog(false);
          setEditSchoolYearDialog(null);
        }}
        onSave={(value) => {
          if (editSchoolYearDialog) {
            handleUpdateSchoolYear(editSchoolYearDialog._id, value);
          } else {
            handleAddSchoolYear(value);
          }
        }}
      />

      <SimpleDialog
        open={addLectureDialog || !!editLectureDialog}
        title={editLectureDialog ? 'Edit Lecture' : 'Add Lecture'}
        label="Lecture Name"
        value={editLectureDialog?.name || ''}
        onClose={() => {
          setAddLectureDialog(false);
          setEditLectureDialog(null);
        }}
        onSave={(value) => {
          if (editLectureDialog) {
            handleUpdateLecture(editLectureDialog._id, value);
          } else {
            handleAddLecture(value);
          }
        }}
      />

      <WordDialog
        open={addWordDialog || !!editWordDialog}
        word={editWordDialog}
        nativeLanguage={selectedLanguage?.nativeLanguage?.name || selectedLanguage?.nativeLanguage}
        foreignLanguage={selectedLanguage?.foreignLanguage?.name || selectedLanguage?.foreignLanguage}
        nativeLanguageIso={selectedLanguage?.nativeLanguage?.iso}
        foreignLanguageIso={selectedLanguage?.foreignLanguage?.iso}
        nativeLanguageSpecialChars={selectedLanguage?.nativeLanguage?.specialCharacters}
        foreignLanguageSpecialChars={selectedLanguage?.foreignLanguage?.specialCharacters}
        onClose={() => {
          setAddWordDialog(false);
          setEditWordDialog(null);
        }}
        onSave={(nativeWord, foreignWord) => {
          if (editWordDialog) {
            handleUpdateWord(editWordDialog._id, nativeWord, foreignWord);
          } else {
            handleAddWord(nativeWord, foreignWord);
          }
        }}
        onSaveAndNew={(nativeWord, foreignWord) => {
          handleAddWordAndNew(nativeWord, foreignWord);
        }}
      />

      <DeleteConfirmDialog
        open={!!deleteConfirmDialog}
        item={deleteConfirmDialog}
        onClose={() => setDeleteConfirmDialog(null)}
        onConfirm={() => {
          const { type, id } = deleteConfirmDialog;
          if (type === 'language') handleDeleteLanguage(id);
          else if (type === 'schoolYear') handleDeleteSchoolYear(id);
          else if (type === 'lecture') handleDeleteLecture(id);
          else if (type === 'word') handleDeleteWord(id);
        }}
      />

      <LearningModeDialog
        open={!!learningModeDialog}
        lecture={learningModeDialog?.lecture}
        language={learningModeDialog?.language}
        onClose={() => setLearningModeDialog(null)}
      />

      {/* Notifications */}
      {notify.notifications.map(notification => (
        <Notification
          key={notification.id}
          message={notification.message}
          type={notification.type}
          duration={notification.duration}
          onClose={() => notify.removeNotification(notification.id)}
        />
      ))}
    </Box>
  );
};

// Dialog Components
const LanguageDialog = ({ open, language, languageMasters, onClose, onSave }) => {
  const [formData, setFormData] = useState({ name: '', nativeLanguage: '', foreignLanguage: '' });

  useEffect(() => {
    if (language) {
      setFormData({
        name: language.name,
        nativeLanguage: language.nativeLanguage?._id || language.nativeLanguage || '',
        foreignLanguage: language.foreignLanguage?._id || language.foreignLanguage || '',
      });
    } else {
      // Set defaults to first language master if available
      const defaultLang = languageMasters.length > 0 ? languageMasters[0]._id : '';
      setFormData({ name: '', nativeLanguage: defaultLang, foreignLanguage: defaultLang });
    }
  }, [language, languageMasters, open]);

  const handleSave = () => {
    if (!formData.name.trim() || !formData.nativeLanguage || !formData.foreignLanguage) return;
    onSave(formData);
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle>{language ? 'Edit Language' : 'Add Language'}</DialogTitle>
      <DialogContent>
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, pt: 1 }}>
          <TextField
            label="Language Name"
            value={formData.name}
            onChange={(e) => setFormData({ ...formData, name: e.target.value })}
            fullWidth
            autoFocus
            helperText="e.g., 'English - German', 'French Basics'"
          />
          <TextField
            select
            label="Native Language"
            value={formData.nativeLanguage}
            onChange={(e) => setFormData({ ...formData, nativeLanguage: e.target.value })}
            fullWidth
          >
            {languageMasters.map(master => (
              <MenuItem key={master._id} value={master._id}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <span className={`fi fi-${master.iso?.toLowerCase()}`} style={{ fontSize: '1.2em' }}></span>
                  <span>{master.name}</span>
                </Box>
              </MenuItem>
            ))}
          </TextField>
          <TextField
            select
            label="Foreign Language"
            value={formData.foreignLanguage}
            onChange={(e) => setFormData({ ...formData, foreignLanguage: e.target.value })}
            fullWidth
          >
            {languageMasters.map(master => (
              <MenuItem key={master._id} value={master._id}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <span className={`fi fi-${master.iso?.toLowerCase()}`} style={{ fontSize: '1.2em' }}></span>
                  <span>{master.name}</span>
                </Box>
              </MenuItem>
            ))}
          </TextField>
        </Box>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Cancel</Button>
        <Button onClick={handleSave} variant="contained" disabled={!formData.name.trim() || !formData.nativeLanguage || !formData.foreignLanguage}>
          {language ? 'Save' : 'Add'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

const SimpleDialog = ({ open, title, label, value: initialValue, onClose, onSave }) => {
  const [value, setValue] = useState('');

  useEffect(() => {
    setValue(initialValue || '');
  }, [initialValue, open]);

  const handleSave = () => {
    if (!value.trim()) return;
    onSave(value);
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle>{title}</DialogTitle>
      <DialogContent>
        <TextField
          label={label}
          value={value}
          onChange={(e) => setValue(e.target.value)}
          fullWidth
          autoFocus
          sx={{ mt: 1 }}
        />
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Cancel</Button>
        <Button onClick={handleSave} variant="contained">Save</Button>
      </DialogActions>
    </Dialog>
  );
};

const WordDialog = ({ open, word, nativeLanguage, foreignLanguage, nativeLanguageIso, foreignLanguageIso, nativeLanguageSpecialChars, foreignLanguageSpecialChars, onClose, onSave, onSaveAndNew }) => {
  const [formData, setFormData] = useState({ nativeWord: '', foreignWord: '' });
  const [swapped, setSwapped] = useState(() => {
    // Load swap preference from cookie
    const savedSwap = document.cookie
      .split('; ')
      .find(row => row.startsWith('wordInputSwapped='))
      ?.split('=')[1];
    return savedSwap === 'true';
  });

  // Refs for input fields to maintain cursor position
  const firstInputRef = React.useRef(null);
  const secondInputRef = React.useRef(null);

  useEffect(() => {
    if (word) {
      setFormData({ nativeWord: word.nativeWord, foreignWord: word.foreignWord });
    } else {
      setFormData({ nativeWord: '', foreignWord: '' });
    }
  }, [word, open]);

  const handleSwap = () => {
    const newSwapped = !swapped;
    setSwapped(newSwapped);
    // Save preference to cookie (expires in 1 year)
    const expiryDate = new Date();
    expiryDate.setFullYear(expiryDate.getFullYear() + 1);
    document.cookie = `wordInputSwapped=${newSwapped}; expires=${expiryDate.toUTCString()}; path=/`;
  };

  const insertSpecialChar = (char, fieldName, inputRef) => {
    const input = inputRef.current?.querySelector('input');
    if (!input) {
      // If ref doesn't work, just append to the end
      setFormData({ ...formData, [fieldName]: formData[fieldName] + char });
      return;
    }

    const cursorPos = input.selectionStart || 0;
    const currentValue = formData[fieldName];
    const newValue = currentValue.slice(0, cursorPos) + char + currentValue.slice(cursorPos);

    setFormData({ ...formData, [fieldName]: newValue });

    // Set cursor position after the inserted character
    setTimeout(() => {
      input.focus();
      input.setSelectionRange(cursorPos + char.length, cursorPos + char.length);
    }, 0);
  };

  const handleSave = () => {
    if (!formData.nativeWord.trim() || !formData.foreignWord.trim()) return;
    onSave(formData.nativeWord, formData.foreignWord);
  };

  const handleSaveAndNew = () => {
    if (!formData.nativeWord.trim() || !formData.foreignWord.trim()) return;
    onSaveAndNew(formData.nativeWord, formData.foreignWord);
    // Reset form for next word
    setFormData({ nativeWord: '', foreignWord: '' });
  };

  // Determine display order based on swap state
  const firstLanguage = swapped ? foreignLanguage : nativeLanguage;
  const firstLanguageIso = swapped ? foreignLanguageIso : nativeLanguageIso;
  const firstLanguageSpecialChars = swapped ? foreignLanguageSpecialChars : nativeLanguageSpecialChars;
  const firstFieldValue = swapped ? formData.foreignWord : formData.nativeWord;
  const firstFieldName = swapped ? 'foreignWord' : 'nativeWord';

  const secondLanguage = swapped ? nativeLanguage : foreignLanguage;
  const secondLanguageIso = swapped ? nativeLanguageIso : foreignLanguageIso;
  const secondLanguageSpecialChars = swapped ? nativeLanguageSpecialChars : foreignLanguageSpecialChars;
  const secondFieldValue = swapped ? formData.nativeWord : formData.foreignWord;
  const secondFieldName = swapped ? 'nativeWord' : 'foreignWord';

  return (
    <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
      <DialogTitle>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <span>{word ? 'Edit Word' : 'Add Word'}</span>
          {firstLanguageIso && secondLanguageIso && (
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, ml: 'auto' }}>
              <span className={`fi fi-${firstLanguageIso.toLowerCase()}`} style={{ fontSize: '1.2em' }}></span>
              <Typography variant="body2" color="text.secondary">→</Typography>
              <span className={`fi fi-${secondLanguageIso.toLowerCase()}`} style={{ fontSize: '1.2em' }}></span>
            </Box>
          )}
        </Box>
      </DialogTitle>
      <DialogContent>
        <Box sx={{ display: 'flex', gap: 2, pt: 1, alignItems: 'flex-start' }}>
          <Box sx={{ flex: 1 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mb: 0.5 }}>
              {firstLanguageIso && (
                <span className={`fi fi-${firstLanguageIso.toLowerCase()}`} style={{ fontSize: '1em' }}></span>
              )}
              <Typography variant="caption" color="text.secondary">
                {firstLanguage || 'Language'}
              </Typography>
            </Box>
            <TextField
              ref={firstInputRef}
              value={firstFieldValue}
              onChange={(e) => setFormData({ ...formData, [firstFieldName]: e.target.value })}
              fullWidth
              autoFocus
              placeholder={`Enter ${firstLanguage || 'word'}`}
              onKeyPress={(e) => {
                if (e.key === 'Enter' && e.ctrlKey) {
                  handleSaveAndNew();
                } else if (e.key === 'Enter') {
                  handleSave();
                }
              }}
            />
            {firstLanguageSpecialChars && firstLanguageSpecialChars.trim() && (
              <Box sx={{ display: 'flex', gap: 0.5, flexWrap: 'wrap', mt: 0.5 }}>
                {firstLanguageSpecialChars.split(' ').filter(char => char.trim()).map((char, idx) => (
                  <Button
                    key={idx}
                    size="small"
                    variant="outlined"
                    onClick={() => insertSpecialChar(char, firstFieldName, firstInputRef)}
                    sx={{ minWidth: 'auto', p: 0.5, fontSize: '0.875rem' }}
                  >
                    {char}
                  </Button>
                ))}
              </Box>
            )}
          </Box>
          <IconButton
            onClick={handleSwap}
            color="primary"
            sx={{ mb: 0.5 }}
            title="Swap input fields"
          >
            <SwapHorizIcon />
          </IconButton>
          <Box sx={{ flex: 1 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mb: 0.5 }}>
              {secondLanguageIso && (
                <span className={`fi fi-${secondLanguageIso.toLowerCase()}`} style={{ fontSize: '1em' }}></span>
              )}
              <Typography variant="caption" color="text.secondary">
                {secondLanguage || 'Language'}
              </Typography>
            </Box>
            <TextField
              ref={secondInputRef}
              value={secondFieldValue}
              onChange={(e) => setFormData({ ...formData, [secondFieldName]: e.target.value })}
              fullWidth
              placeholder={`Enter ${secondLanguage || 'word'}`}
              onKeyPress={(e) => {
                if (e.key === 'Enter' && e.ctrlKey) {
                  handleSaveAndNew();
                } else if (e.key === 'Enter') {
                  handleSave();
                }
              }}
            />
            {secondLanguageSpecialChars && secondLanguageSpecialChars.trim() && (
              <Box sx={{ display: 'flex', gap: 0.5, flexWrap: 'wrap', mt: 0.5 }}>
                {secondLanguageSpecialChars.split(' ').filter(char => char.trim()).map((char, idx) => (
                  <Button
                    key={idx}
                    size="small"
                    variant="outlined"
                    onClick={() => insertSpecialChar(char, secondFieldName, secondInputRef)}
                    sx={{ minWidth: 'auto', p: 0.5, fontSize: '0.875rem' }}
                  >
                    {char}
                  </Button>
                ))}
              </Box>
            )}
          </Box>
        </Box>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Cancel</Button>
        {!word && (
          <Button onClick={handleSaveAndNew} variant="outlined" color="primary">
            Add and New
          </Button>
        )}
        <Button onClick={handleSave} variant="contained">{word ? 'Save' : 'Add'}</Button>
      </DialogActions>
    </Dialog>
  );
};

const DeleteConfirmDialog = ({ open, item, onClose, onConfirm }) => {
  if (!item) return null;

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle color="error.main">Confirm Delete</DialogTitle>
      <DialogContent>
        <Typography>
          Are you sure you want to delete this {item.type}?
        </Typography>
        <Typography sx={{ mt: 1, fontWeight: 500 }}>
          {item.name}
        </Typography>
        <Typography variant="body2" color="text.secondary" sx={{ mt: 2 }}>
          This action cannot be undone.
        </Typography>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Cancel</Button>
        <Button onClick={onConfirm} variant="contained" color="error">
          Delete
        </Button>
      </DialogActions>
    </Dialog>
  );
};

const LearningModeDialog = ({ open, lecture, language, onClose }) => {
  const [words, setWords] = useState([]);
  const [currentWord, setCurrentWord] = useState(null);
  const [showTranslation, setShowTranslation] = useState(false);
  const [revealTime, setRevealTime] = useState(3);
  const [nextDelay, setNextDelay] = useState(3);
  const [autoMode, setAutoMode] = useState(false);
  const [swapped, setSwapped] = useState(false);
  const [loading, setLoading] = useState(false);
  const [revealProgress, setRevealProgress] = useState(0);
  const [nextProgress, setNextProgress] = useState(0);
  const [wordsRevealed, setWordsRevealed] = useState(0);

  // Use a ref to store words so pickRandomWord doesn't need words as a dependency
  const wordsRef = React.useRef([]);
  wordsRef.current = words;

  // pickRandomWord uses the ref so it's stable and doesn't cause re-renders
  const pickRandomWord = useCallback((wordList) => {
    const list = wordList || wordsRef.current;
    if (list.length === 0) return;
    const randomIndex = Math.floor(Math.random() * list.length);
    setCurrentWord(list[randomIndex]);
    setShowTranslation(false);
    setRevealProgress(0);
    setNextProgress(0);
  }, []);

  // Load words when dialog opens
  useEffect(() => {
    const loadWords = async () => {
      if (open && lecture?._id) {
        setLoading(true);
        try {
          const data = await fetchWords(lecture._id);
          setWords(data);
          if (data.length > 0) {
            // Pick first random word directly here
            const randomIndex = Math.floor(Math.random() * data.length);
            setCurrentWord(data[randomIndex]);
            setShowTranslation(false);
            setRevealProgress(0);
            setNextProgress(0);
          }
        } catch (err) {
          console.error('Failed to load words for learning mode:', err);
        } finally {
          setLoading(false);
        }
      }
    };
    loadWords();
  }, [open, lecture]);

  // Reset state when dialog closes
  useEffect(() => {
    if (!open) {
      setShowTranslation(false);
      setCurrentWord(null);
      setRevealProgress(0);
      setNextProgress(0);
      setWordsRevealed(0);
      setWords([]);
    }
  }, [open]);

  // Auto-reveal timer with progress
  useEffect(() => {
    if (!open || !currentWord || showTranslation) return;

    setRevealProgress(0);
    const totalMs = revealTime * 1000;
    const intervalMs = 50;
    let elapsed = 0;

    const interval = setInterval(() => {
      elapsed += intervalMs;
      const progress = Math.min((elapsed / totalMs) * 100, 100);
      setRevealProgress(progress);

      if (elapsed >= totalMs) {
        clearInterval(interval);
        setShowTranslation(true);
        setRevealProgress(100);
        setWordsRevealed(prev => prev + 1);
      }
    }, intervalMs);

    return () => clearInterval(interval);
  }, [open, currentWord, showTranslation, revealTime]);

  // Auto-next timer with progress (when autoMode is enabled and translation is shown)
  useEffect(() => {
    if (!open || !currentWord || !showTranslation || !autoMode) {
      setNextProgress(0);
      return;
    }

    setNextProgress(0);
    const totalMs = nextDelay * 1000;
    const intervalMs = 50;
    let elapsed = 0;

    const interval = setInterval(() => {
      elapsed += intervalMs;
      const progress = Math.min((elapsed / totalMs) * 100, 100);
      setNextProgress(progress);

      if (elapsed >= totalMs) {
        clearInterval(interval);
        pickRandomWord();
      }
    }, intervalMs);

    return () => clearInterval(interval);
  }, [open, currentWord, showTranslation, autoMode, nextDelay, pickRandomWord]);

  const handleNext = () => {
    pickRandomWord();
  };

  const handleSwapDirection = () => {
    setSwapped(!swapped);
  };

  const handleRevealTimeChange = (event, newValue) => {
    setRevealTime(newValue);
  };

  const handleNextDelayChange = (event, newValue) => {
    setNextDelay(newValue);
  };

  if (!lecture || !language) return null;

  const nativeLanguage = language.nativeLanguage;
  const foreignLanguage = language.foreignLanguage;

  // Determine display order based on swap state
  const firstLanguage = swapped ? foreignLanguage : nativeLanguage;
  const secondLanguage = swapped ? nativeLanguage : foreignLanguage;
  const firstWord = currentWord ? (swapped ? currentWord.foreignWord : currentWord.nativeWord) : '';
  const secondWord = currentWord ? (swapped ? currentWord.nativeWord : currentWord.foreignWord) : '';

  return (
    <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
      <DialogTitle>
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
          <Typography variant="h6">Learning Mode</Typography>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
            <Typography variant="body2" color="text.secondary">
              {wordsRevealed} words revealed
            </Typography>
            <IconButton onClick={onClose} size="small">
              <CloseIcon />
            </IconButton>
          </Box>
        </Box>
      </DialogTitle>
      <DialogContent sx={{ overflow: 'visible' }}>
        {loading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
            <Typography color="text.secondary">Loading words...</Typography>
          </Box>
        ) : words.length === 0 ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
            <Typography color="text.secondary">No words in this lecture yet.</Typography>
          </Box>
        ) : (
          <Box sx={{ py: 2 }}>
            {/* Timer Sliders */}
            <Box sx={{ mb: 3 }}>
              {/* Reveal Time Slider */}
              <Tooltip title="Time until the 2nd word will be revealed" arrow placement="top">
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 2 }}>
                  <VisibilityIcon color="action" />
                  <Slider
                    value={revealTime}
                    onChange={handleRevealTimeChange}
                    min={1}
                    max={20}
                    step={1}
                    marks
                    valueLabelDisplay="auto"
                    valueLabelFormat={(value) => `${value}s`}
                    sx={{ flex: 1 }}
                    componentsProps={{
                      tooltip: { style: { zIndex: 1500 } }
                    }}
                  />
                  <Typography variant="body2" color="text.secondary" sx={{ minWidth: 30 }}>
                    {revealTime}s
                  </Typography>
                </Box>
              </Tooltip>

              {/* Next Delay Slider */}
              <Tooltip title="Delay until automatically loading the next word" arrow placement="top">
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 2 }}>
                  <NavigateNextIcon color="action" />
                  <Slider
                    value={nextDelay}
                    onChange={handleNextDelayChange}
                    min={1}
                    max={20}
                    step={1}
                    marks
                    valueLabelDisplay="auto"
                    valueLabelFormat={(value) => `${value}s`}
                    sx={{ flex: 1 }}
                    disabled={!autoMode}
                    componentsProps={{
                      tooltip: { style: { zIndex: 1500 } }
                    }}
                  />
                  <Typography variant="body2" color="text.secondary" sx={{ minWidth: 30 }}>
                    {nextDelay}s
                  </Typography>
                </Box>
              </Tooltip>

              {/* Auto Mode Switch */}
              <Box sx={{ display: 'flex', justifyContent: 'flex-end' }}>
                <FormControlLabel
                  control={
                    <Switch
                      checked={autoMode}
                      onChange={(e) => setAutoMode(e.target.checked)}
                      color="primary"
                    />
                  }
                  label="Auto Mode"
                />
              </Box>
            </Box>

            {/* Language Direction Header */}
            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 2, mb: 3 }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                {firstLanguage?.iso && (
                  <span className={`fi fi-${firstLanguage.iso.toLowerCase()}`} style={{ fontSize: '1.5em' }}></span>
                )}
                <Typography variant="subtitle1">{firstLanguage?.name || 'Native'}</Typography>
              </Box>
              <IconButton onClick={handleSwapDirection} color="primary">
                <SwapHorizIcon />
              </IconButton>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                {secondLanguage?.iso && (
                  <span className={`fi fi-${secondLanguage.iso.toLowerCase()}`} style={{ fontSize: '1.5em' }}></span>
                )}
                <Typography variant="subtitle1">{secondLanguage?.name || 'Foreign'}</Typography>
              </Box>
            </Box>

            {/* Word Display Area */}
            <Box sx={{ display: 'flex', gap: 3, minHeight: 150 }}>
              {/* First Word */}
              <Paper
                elevation={2}
                sx={{
                  flex: 1,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  p: 3,
                  bgcolor: 'primary.light',
                  borderRadius: 2,
                }}
              >
                <Typography variant="h4" sx={{ fontWeight: 500, color: 'primary.contrastText', textAlign: 'center' }}>
                  {firstWord}
                </Typography>
              </Paper>

              {/* Second Word (revealed after timer or click) */}
              <Paper
                elevation={2}
                onClick={() => {
                  if (!showTranslation) {
                    setShowTranslation(true);
                    setWordsRevealed(prev => prev + 1);
                  }
                }}
                sx={{
                  flex: 1,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  p: 3,
                  bgcolor: showTranslation ? 'success.light' : 'grey.200',
                  borderRadius: 2,
                  transition: 'background-color 0.3s ease',
                  cursor: showTranslation ? 'default' : 'pointer',
                  '&:hover': !showTranslation ? {
                    bgcolor: 'grey.300',
                  } : {},
                }}
              >
                {showTranslation ? (
                  <Typography variant="h4" sx={{ fontWeight: 500, color: 'success.contrastText', textAlign: 'center' }}>
                    {secondWord}
                  </Typography>
                ) : (
                  <Typography variant="h5" color="text.secondary" sx={{ fontStyle: 'italic' }}>
                    ?
                  </Typography>
                )}
              </Paper>
            </Box>

            {/* Progress Bars */}
            <Box sx={{ mt: 3, minHeight: 40 }}>
              {/* Reveal Progress */}
              {!showTranslation && (
                <Box>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.5 }}>
                    <VisibilityIcon fontSize="small" color="primary" />
                    <Typography variant="caption" color="text.secondary">
                      Revealing in {Math.ceil(revealTime * (1 - revealProgress / 100))}s
                    </Typography>
                  </Box>
                  <LinearProgress
                    variant="determinate"
                    value={revealProgress}
                    sx={{
                      height: 8,
                      borderRadius: 4,
                      bgcolor: 'grey.200',
                      '& .MuiLinearProgress-bar': {
                        borderRadius: 4,
                        bgcolor: 'primary.main',
                      },
                    }}
                  />
                </Box>
              )}

              {/* Next Word Progress (only when auto mode is on and translation is shown) */}
              {showTranslation && autoMode && (
                <Box>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.5 }}>
                    <NavigateNextIcon fontSize="small" color="success" />
                    <Typography variant="caption" color="text.secondary">
                      Next word in {Math.ceil(nextDelay * (1 - nextProgress / 100))}s
                    </Typography>
                  </Box>
                  <LinearProgress
                    variant="determinate"
                    value={nextProgress}
                    sx={{
                      height: 8,
                      borderRadius: 4,
                      bgcolor: 'grey.200',
                      '& .MuiLinearProgress-bar': {
                        borderRadius: 4,
                        bgcolor: 'success.main',
                      },
                    }}
                  />
                </Box>
              )}
            </Box>
          </Box>
        )}
      </DialogContent>
      <DialogActions sx={{ px: 3, pb: 2 }}>
        <Button onClick={onClose} variant="outlined">
          Close
        </Button>
        <Button
          onClick={handleNext}
          variant="contained"
          endIcon={<NavigateNextIcon />}
          disabled={words.length === 0}
        >
          Next
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default ContentPage;
